/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';
import zip from 'lodash/zip';

import StringUtil from '../string/string-util';
import DhtmlxRecFrequency from './dhtmlx-rec-frequency';

/**
 * @private
 * @typedef {Record<string, *>} DhtmlxRecTypeInfo TODO #555
 */

const baseSeparator = '_';
const baseSeparatorCount = 4;
const count2Index = 3;
const countIndex = 1;
const dayIndex = 2;
const daysIndex = 4;
const daysSeparator = ',';
const extraFinite = '';
const extraInfinite = 'no';
const extraSeparator = '#';
const typeIndex = 0;

// TODO Rozdělit na více modulů
// - Dávalo by to smysl z pohledu single-responsibility, ale zatím od toho bylo
//   opuštěno, kvůli složitosti.
// - Public API rozdělit na DhtmlxRecTypeParser a DhtmlxRecTypeStringifier.
//   Rozdělení provést dle zdejších parse* a stringify* metod.
// - Vyčlenit metody isValid* do samostatné třídy nebo tříd.
// - Sdílet konfiguraci konstant (např. separátory) mezi těmito třídami.
// TODO Parsovat část extra společně s částí base?
// - Tzn. očekávat v řetězci posloupnost oddělovačů 4x čárka a 1x mřížka?
//   Dále již nic neparsovat a dle nich rozdělit řetězec na části.
//   Nepoužíval by se tedy `slice` ale výhradně `indexOf`.
// - Je otázkou, jestli je to tak dobře, protože aktuální zpracování, které
//   nejprve rozdělí řetězec dle mřížky, vychází z oficiální PHP implementace.
// TODO Podpora pro prázdný `rec_type` a pro hodnotu `none`?
// TODO Omezení délky `rec_type` na 50 znaků dle specifikace?
// - https://docs.dhtmlx.com/scheduler/loading_data.html#databasestructure
/**
 * Utility pro práci s DHTMLX `rec_type` řetězci
 *
 * Zdroje:
 * - {@link DhtmlxEvent#rec_type}
 * - [DHTMLX Recurring Events](https://docs.dhtmlx.com/scheduler/recurring_events.html)
 */
class DhtmlxRecTypeUtil {
  /**
   * Určí, jestli je hodnota počtu validní.
   * Lze použít pro položky `count`, `count2` i `count3`.
   * @param {number} count
   * @private
   * @returns {boolean}
   */
  static isValidCount(count) {
    return Number.isInteger(count) && count > 0;
  }

  /**
   * Určí, jestli je hodnota dne v týdnu validní.
   * @param {number} day
   * @private
   * @returns {boolean}
   */
  static isValidDay(day) {
    return Number.isInteger(day) && day > -1 && day < 7;
  }

  /**
   * Určí, jestli je hodnota typu opakování validní.
   * @param {string} type
   * @private
   * @returns {boolean}
   */
  static isValidType(type) {
    return Object.values(DhtmlxRecFrequency).includes(type);
  }

  /**
   * Načte z `rec_type` řetězce informace o opakování, které jsou v něm uložené.
   * @param {string} string
   * @returns {DhtmlxRecTypeInfo}
   */
  static parse(string) {
    const extraIndex = this.parseExtraIndex(string);
    const base = this.parseBase(extraIndex, string);
    const baseParts = this.parseBaseParts(base);
    const extra = this.parseExtra(extraIndex, string);
    return {
      count: this.parseCount(baseParts[countIndex]),
      count2: this.parseCount2(baseParts[count2Index]),
      count3: this.parseCount3(extra),
      day: this.parseDay(baseParts[dayIndex]),
      days: this.parseDays(baseParts[daysIndex]),
      infinite: this.parseInfinite(extra),
      type: this.parseType(baseParts[typeIndex])
    };
  }

  /**
   * Načte základ `rec_type`, který se nachází mezi začátkem řetězce
   * a začátkem oddělovače části extra.
   * @param {number} extraIndex
   * @param {string} string
   * @private
   * @returns {string}
   */
  static parseBase(extraIndex, string) {
    return string.slice(0, extraIndex);
  }

  /**
   * Načte jednotlivé části základ `rec_type`, který jsou oddělené čárkou.
   * @param {string} base
   * @private
   * @returns {string[]}
   */
  static parseBaseParts(base) {
    const baseSeparatorLength = baseSeparator.length;
    // Vyhledají se indexy v řetězci base, na kterých začínají separátory.
    // Hledá se do počtu o jeden separátor více, než se očekává,
    // aby se zkontrolovalo, že v řetězci již není více separátorů.
    // Šlo by použít i String#slice, ale ten nelze zastavit po překročení
    // očekávané délky pole.
    const separatorsStartIndexes = StringUtil.indexesOf(base, baseSeparator, {
      max: baseSeparatorCount + 1
    });
    const separatorsCount = separatorsStartIndexes.length;
    if (separatorsCount !== baseSeparatorCount) {
      throw new Error(`Invalid base separator count "${base}".`);
    }
    // Přičtením délky separátoru se určí indexy, kde separátory končí.
    const separatorsEndIndexes = separatorsStartIndexes.map(
      (index) => index + baseSeparatorLength
    );
    // Počátky částí base, které se hledají, jsou na začátku řetězce base
    // a na konci každého separátoru, který části odděluje.
    const partsStartIndexes = [0, ...separatorsEndIndexes];
    // Konce částí base, které se hledají, jsou na začátku každého separátoru
    // a na konci řetězce base, který části odděluje.
    const partsEndIndexes = [...separatorsStartIndexes, base.length];
    return (
      // Všechny počátky částí se spojí s odpovídajícími konci částí.
      zip(partsStartIndexes, partsEndIndexes)
        // Jednotlivé části se vyřežou dle jejich počátečního a koncového indexu.
        .map(([startIndex, endIndex]) => base.slice(startIndex, endIndex))
    );
  }

  /**
   * Načte interval opakování události (např. každé 3 dny).
   * @param {string} countString
   * @private
   * @returns {number}
   */
  static parseCount(countString) {
    if (countString.length === 0) {
      throw new Error('Missing count.');
    }
    const count = Number(countString);
    if (!this.isValidCount(count)) {
      throw new Error(`Invalid count "${countString}".`);
    }
    return count;
  }

  /**
   * Načte pořadí týdne v měsíci, ve který se opakuje
   * (např. každou 2. neděli v měsíci).
   * @param {string} count2String
   * @private
   * @returns {number | undefined}
   */
  static parseCount2(count2String) {
    if (count2String.length === 0) {
      return;
    }
    const count2 = Number(count2String);
    if (!this.isValidCount(count2)) {
      throw new Error(`Invalid count2 "${count2String}".`);
    }
    return count2;
  }

  /**
   * Načte počet opakování události (např. maximálně 5 opakování události).
   * @param {string} extra
   * @private
   * @returns {number | undefined}
   */
  static parseCount3(extra) {
    const invalidValues = [extraFinite, extraInfinite];
    if (invalidValues.includes(extra)) {
      return;
    }
    const count3 = Number(extra);
    if (!this.isValidCount(count3)) {
      throw new Error(`Invalid count3 "${extra}".`);
    }
    return count3;
  }

  /**
   * Načte den v týdnu, ve který se opakuje (např. každé úterý).
   * @param {string} dayString
   * @private
   * @returns {number | undefined}
   */
  static parseDay(dayString) {
    if (dayString.length === 0) {
      return;
    }
    const day = Number(dayString);
    if (!this.isValidDay(day)) {
      throw new Error(`Invalid day "${dayString}".`);
    }
    return day;
  }

  /**
   * Načte dny v týdnu, ve které se opakuje (např. každou sobotu a neděli).
   * @param {string} daysString
   * @private
   * @returns {number[]}
   */
  static parseDays(daysString) {
    return daysString.split(daysSeparator).reduce((acc, dayString) => {
      const day = this.parseDay(dayString);
      // Některé aplikace produkují seznamy jako "1,,3,4".
      // Je sporné, jestli je to dle specifikace,
      // ale tato varianta se raději podporuje.
      if (!isNil(day)) {
        acc.push(day);
      }
      return acc;
    }, /** @type {number[]} */ ([]));
  }

  /**
   * Načte extra část `rec_type` řetězce, která se nachází mezi koncem
   * oddělovače části extra a koncem řetězce.
   * @param {number} extraIndex
   * @param {string} string
   * @private
   * @returns {string}
   */
  static parseExtra(extraIndex, string) {
    return string.slice(extraIndex + extraSeparator.length);
  }

  /**
   * Načte index začátku oddělovače základní a extra části `rec_type` řetězce.
   * @param {string} string
   * @private
   * @returns {number}
   */
  static parseExtraIndex(string) {
    const extraIndex = string.indexOf(extraSeparator);
    if (extraIndex === -1) {
      throw new Error(`Missing extra separator in "${string}".`);
    }
    return extraIndex;
  }

  /**
   * Načte příznak, jestli má opakování konec.
   * @param {string} extra
   * @private
   * @returns {boolean}
   */
  static parseInfinite(extra) {
    return extra === extraInfinite;
  }

  /**
   * Načte typ opakování (např. měsíčně).
   * @param {string} type
   * @private
   * @returns {DhtmlxRecFrequency}
   */
  static parseType(type) {
    if (!this.isValidType(type)) {
      throw new Error(`Invalid type "${type}".`);
    }
    return type;
  }

  /**
   * Převede informace o opakování na `rec_type` řetězec.
   * @param {Object} info Informace o opakování
   * s možností nevyplnit některé hodnoty a nahradit je výchozími.
   * TODO #555: Typ by měl být DhtmlxRecTypeInfo.
   * @param {number} [info.count]
   * @param {number} [info.count2]
   * @param {number} [info.count3]
   * @param {number} [info.day]
   * @param {number[]} [info.days]
   * @param {boolean} [info.infinite]
   * @param {DhtmlxRecFrequency} info.type
   * @returns {string}
   */
  static stringify({
    count = 1,
    count2,
    count3,
    day,
    days = [],
    infinite = false,
    type
  }) {
    const info = { count, count2, count3, day, days, infinite, type };
    return this.stringifyInfo(info);
  }

  /**
   * Převede informace o opakování na řetězec se základem `rec_type`,
   * který se nachází mezi začátkem řetězce a začátkem oddělovače části extra.
   * @param {DhtmlxRecTypeInfo} info
   * @private
   * @returns {string}
   */
  static stringifyBase(info) {
    const parts = [
      this.stringifyType(info),
      this.stringifyCount(info),
      this.stringifyDay(info),
      this.stringifyCount2(info),
      this.stringifyDays(info)
    ];
    return parts.join(baseSeparator);
  }

  /**
   * Převede informace o opakování na řetězec s intervalem opakování události.
   * (např. každé 3 dny).
   * @param {DhtmlxRecTypeInfo} info
   * @private
   * @returns {string}
   */
  static stringifyCount(info) {
    const { count } = info;
    if (!this.isValidCount(count)) {
      throw new Error(`Invalid count "${count}".`);
    }
    return String(count);
  }

  /**
   * Převede informace o opakování na řetězec s pořadím týdne v měsíci,
   * ve který se opakuje (např. každou 2. neděli v měsíci).
   * @param {DhtmlxRecTypeInfo} info
   * @private
   * @returns {string}
   */
  static stringifyCount2(info) {
    const { count2 } = info;
    if (isNil(count2)) {
      return '';
    }
    if (!this.isValidCount(count2)) {
      throw new Error(`Invalid count2 "${count2}".`);
    }
    return String(count2);
  }

  /**
   * Převede informace o opakování na řetězec se dnem v týdnu,
   * ve který se opakuje (např. každé úterý).
   * @param {DhtmlxRecTypeInfo} info
   * @private
   * @returns {string}
   */
  static stringifyDay(info) {
    const { day } = info;
    if (isNil(day)) {
      return '';
    }
    return this.stringifyDayValue(day);
  }

  /**
   * Převede den v týdnu na řetězec.
   * @param {number} day
   * @private
   * @returns {string}
   */
  static stringifyDayValue(day) {
    if (!this.isValidDay(day)) {
      throw new Error(`Invalid day "${day}".`);
    }
    return String(day);
  }

  /**
   * Převede informace o opakování na řetězec se dny v týdnu,
   * ve které se opakuje (např. každou sobotu a neděli).
   * @param {DhtmlxRecTypeInfo} info
   * @private
   * @returns {string}
   */
  static stringifyDays(info) {
    return info.days
      .map((day) => this.stringifyDayValue(day))
      .join(daysSeparator);
  }

  /**
   * Převede informace o opakování na řetězec s extra částí `rec_type`,
   * která se nachází mezi koncem oddělovače části extra a koncem řetězce.
   * @param {DhtmlxRecTypeInfo} info
   * @private
   * @returns {string}
   */
  static stringifyExtra(info) {
    const { count3, infinite } = info;
    if (isNil(count3)) {
      if (infinite) {
        return extraInfinite;
      }
      return extraFinite;
    }
    if (infinite) {
      throw new Error('Unable to create infinite recurrence with end count.');
    }
    return String(count3);
  }

  /**
   * Převede informace o opakování na `rec_type` řetězec bez použití
   * výchozích hodnot.
   * @param {DhtmlxRecTypeInfo} info
   * @private
   * @returns {string}
   */
  static stringifyInfo(info) {
    const parts = [this.stringifyBase(info), this.stringifyExtra(info)];
    return parts.join(extraSeparator);
  }

  /**
   * Převede informace o opakování na řetězec s typem opakování (např. měsíčně).
   * @param {DhtmlxRecTypeInfo} info
   * @private
   * @returns {string}
   */
  static stringifyType(info) {
    const { type } = info;
    if (!this.isValidType(type)) {
      throw new Error(`Invalid type "${type}".`);
    }
    return type;
  }
}

export default DhtmlxRecTypeUtil;
